import { Blocks } from "lucide-react";

import {
  AGENTIC_LOGIC_PUBLISH_GATE_ORDER,
  AGENTIC_LOGIC_TEMPLATE_RUNNER_DEFAULT_BUDGET_MS,
  type AgenticLogicBudgetBasis,
  AgenticLogicBudgetBasisList,
  AgenticLogicPublishGateKey,
  AgenticLogicRequiredTestKey,
  AgenticLogicRequiredTestKeyList,
  type AgenticLogicSurface,
  AgenticLogicSurfaceList,
  renderAgenticLogicBudgetBasis,
  renderAgenticLogicRequestContract,
  renderAgenticLogicRequestFieldNotes,
  renderAgenticLogicSdkFieldNotes,
} from "@intelligems/ig-types";
import {
  AGENTIC_LOGIC_SDK_AMBIENT_DTS,
  AGENTIC_LOGIC_STARTER_SOURCE,
  type AgenticLogicDataSurface,
  buildAgenticLogicTypesModuleSource,
} from "@intelligems/ig-utils";
import { IgChip, IgTypography } from "@intelligems/ig-theme";
import { Stack } from "@mui/material";

import type {
  WorkspaceBrainConfig,
  WorkspaceChromeConfig,
  WorkspaceControlsPanelConfig,
  WorkspaceDomainConfig,
  WorkspaceEditorTestsConfig,
  WorkspaceFilesConfig,
  WorkspaceGatesConfig,
  WorkspaceGateSpec,
  WorkspacePublishBlockCopy,
  WorkspacePublishReviewConfig,
  WorkspaceRequiredCheckSpec,
  WorkspaceTestFilesConfig,
} from "@src/features/agenticCodegen/workspace/domainConfig";
import { ReviewRow } from "@src/features/agenticCodegen/workspace/components/modals/ReviewRow";
import type {
  PublishBlockReason,
  WorkspaceDependency,
  WorkspaceVersion,
} from "@src/features/agenticCodegen/workspace/types";
import type { WorkspaceCapabilities } from "@src/features/agenticCodegen/workspace/apiPort";

import {
  AGENTIC_LOGIC_ENTRY_FILENAME,
  AGENTIC_LOGIC_SDK_TYPES_FILENAME,
  AGENTIC_LOGIC_TYPES_FILENAME,
  buildAgenticLogicMonacoConfig,
  decodeAgenticLogicFiles,
} from "./agenticLogicMonacoTypes";
import { AGENTIC_LOGIC_USER_SUITE_SKELETON } from "./agenticLogicTestSuiteTypes";
import type { AgenticLogicTabKey } from "./types";

// ---------------------------------------------------------------------------
// The Agentic Logic binding of the shared workspace's domain config.
//
// The static halves are exported as module constants so `agenticDomain.spec`
// can assert them without constructing a view model — the factory below exists
// only because tab renderers and publish-review rows close over live state.
// ---------------------------------------------------------------------------

const GATE_LABELS: Record<
  AgenticLogicPublishGateKey,
  { short: string; full: string }
> = {
  static_contract: { short: "Code", full: "Code is ready" },
  deps_locked: { short: "Tools", full: "Tools are ready" },
  org_secrets_resolved: { short: "Saved info", full: "Saved info is ready" },
  required_tests_passed: { short: "Safety", full: "Safety checks pass" },
  bundle_size: { short: "Code size", full: "Code is not too large" },
  runtime_timeout: { short: "Speed", full: "Code runs fast enough" },
  chain_cost: { short: "Steps", full: "Steps are within the limit" },
  scan_cost: { short: "Data use", full: "Data use is within the limit" },
  graph_consistency: { short: "Data", full: "Connected data matches" },
  ai_tests_generated: { short: "Make tests", full: "Extra tests are ready" },
  ai_tests_passed: { short: "Extra tests", full: "Extra tests pass" },
  user_tests_passed: { short: "Your tests", full: "Your tests pass" },
  lambda_deploy: { short: "Go live", full: "Publish rule" },
};

/**
 * The merchant-facing name of the check that refused, for a reader who is never
 * shown the gate rail. `lambda_deploy`'s full label names the platform verb
 * ("Publish rule"), which is exactly the word basic mode does not use, so it is
 * the one key reworded here.
 */
export const agenticGateFailureLabel = (
  key: AgenticLogicPublishGateKey
): string =>
  key === "lambda_deploy" ? "Preparing rule" : GATE_LABELS[key].full;

// ---------------------------------------------------------------------------
// Per-gate detailsJson renderers, keyed on the closed `reason` vocabulary the
// backend stamps into every failing gate's details. Each returns what to
// CHANGE, in the author's language — never the runner's thrown sentence,
// which the shared rail otherwise falls back to verbatim (stack and all).
// Returning null falls back to the gate's plain `result` string, which is the
// right answer for passed gates and for rows written before the vocabulary.
// ---------------------------------------------------------------------------

type GateDetails = Record<string, unknown>;
type GateDetailFormatter = (details: GateDetails) => string | null;

const asString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;
const asNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const asStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
const bulleted = (lines: string[]): string =>
  lines.map((line) => `• ${line}`).join("\n");
const asRecord = (value: unknown): GateDetails =>
  value && typeof value === "object" ? (value as GateDetails) : {};

/**
 * The budget disclosure a budget-bearing gate carries, rendered by ig-types.
 *
 * Read defensively: `budgetSurfaces` and `budgetBasis` are additive, so a job
 * row written before them arrives with neither, and the renderer's own
 * legacy branch then makes no claim about where the budget came from. This
 * consumer must never supply the missing claim itself — inventing "assigned"
 * for a rule assigned nowhere is exactly the defect this replaced.
 */
const asBudgetBasis = (value: unknown): AgenticLogicBudgetBasis | undefined =>
  AgenticLogicBudgetBasisList.includes(value as AgenticLogicBudgetBasis)
    ? (value as AgenticLogicBudgetBasis)
    : undefined;

const formatBudgetBasis = (details: GateDetails): string | null => {
  const budgetMs = asNumber(details.budgetMs);
  if (budgetMs === null) return null;
  return renderAgenticLogicBudgetBasis({
    budgetMs,
    surfaces: asStringList(details.budgetSurfaces).filter(
      (surface): surface is AgenticLogicSurface =>
        AgenticLogicSurfaceList.includes(surface as AgenticLogicSurface)
    ),
    basis: asBudgetBasis(details.budgetBasis),
  });
};

/**
 * The one reason every gate can carry: the gate body threw something the
 * vocabulary does not classify. That is a platform gap, not an authoring
 * mistake, and the copy must not send the author hunting through their rule.
 */
const formatUnexpectedError = (details: GateDetails): string | null => {
  if (details.reason !== "unexpected_error") return null;
  const message = asString(details.message) ?? "an internal error";
  return (
    `The checks hit an internal error: ${message}\n` +
    "Re-run the tests. If this repeats, it is a platform fault — your rule is not the cause."
  );
};

const formatStaticContractDetails: GateDetailFormatter = (details) => {
  if (details.reason === "source_guard_refusal") {
    const construct = asString(details.construct);
    const remedy = asString(details.remedy);
    const modules = asStringList(details.modules);
    const lines: string[] = [];
    if (construct) lines.push(`Remove ${construct} from the rule code.`);
    if (modules.length > 0)
      lines.push(`Remove the disallowed imports: ${modules.join(", ")}.`);
    const parseError = asString(details.parseError);
    if (parseError) lines.push(`The source did not parse: ${parseError}`);
    if (remedy) lines.push(remedy);
    return lines.length > 0 ? lines.join("\n") : null;
  }
  if (details.reason === "missing_evaluate_export") {
    const signature =
      asString(details.expectedSignature) ??
      "export async function evaluate(invocation)";
    return `The entry module must export exactly \`${signature}\` — a default export or a renamed function is not served.`;
  }
  return formatUnexpectedError(details);
};

const formatChainCostDetails: GateDetailFormatter = (details) => {
  // Historical jobs only — the publish pipeline stopped emitting this when the
  // per-invocation attempt ceiling moved into the chain runtime.
  if (details.reason === "refused_unbounded") {
    const detail = asString(details.detail);
    return [
      detail,
      "This check no longer runs: a chain is now bounded automatically at a fixed number of attempts per shopper.",
    ]
      .filter((line): line is string => line !== null)
      .join("\n");
  }
  if (details.reason === "measurement_failed") {
    const scenario = asString(details.scenarioName);
    const cause = asString(details.cause);
    return (
      `The rule threw while every fallback branch was exercised${scenario ? ` on scenario “${scenario}”` : ""}` +
      `${cause ? `: ${cause}` : "."}\n` +
      "Fix that error first — a chain that cannot finish cannot be priced."
    );
  }
  // The cardinality failure and the latency failure have OPPOSITE fixes: the
  // remedy below ("make its reads cheaper", i.e. run them in parallel) is
  // precisely what turns a conditional fan-out into an unconditional one and
  // makes the read count worse. Same gate key, different advice.
  if (details.reason === "reads_over_budget") {
    const worstCase = asNumber(details.worstCaseReads);
    const ceiling = asNumber(details.readCeiling);
    const perSeed = asNumber(details.perSeedReads);
    const seeds = asNumber(details.distinctRequestSeeds);
    const universe = asNumber(details.requestSeedUniverse);
    const cause = asString(details.cause);
    // The two refusals have DIFFERENT remedies. A chain fan-out refused for
    // following the cart is not fixed by shrinking a cap that is already
    // below the cart — that advice is what kept the repair loop spinning.
    // Job rows written before `cause` existed fall through to the generic
    // copy rather than being told the wrong thing confidently.
    if (cause === "chain_seed_fanout") {
      const inChainSeeds = asNumber(details.distinctRequestSeedsInsideChain);
      const perAttempt = asNumber(details.chainReadsPerAttempt);
      return [
        "This rule's fan-out inside its fallback chain follows the cart, so it reads more per shopper as the cart grows.",
        inChainSeeds !== null && universe !== null && perAttempt !== null
          ? `It read ${inChainSeeds} of the scenario's ${universe} cart products inside chain attempts, at ${perAttempt} read(s) per attempt.`
          : null,
        worstCase !== null && ceiling !== null
          ? `At a ${ceiling}-product cart that prices at ${worstCase} reads, over the ${ceiling}-read limit for one invocation.`
          : null,
        "Cap the seed list to a literal maximum below a cart before the reads, or give each `attempt()` at most ONE read so the attempt ceiling trims it.",
        "Shrinking a cap that is already smaller than the cart will not clear this, and the read past the limit fails the whole invocation rather than trimming it.",
      ]
        .filter((line): line is string => line !== null)
        .join("\n");
    }
    const headline =
      worstCase === null || ceiling === null
        ? "This rule reads more per shopper than one invocation is allowed."
        : `This rule prices at ${worstCase} reads per shopper, over the ${ceiling}-read limit for one invocation.`;
    const observed =
      perSeed !== null && seeds !== null && universe !== null
        ? `It issued ${perSeed} read(s) per cart product and read ${seeds} of the scenario's ${universe} cart products, so its reads grow with the cart.`
        : null;
    return [
      headline,
      observed,
      "Cap the seed list to a literal maximum smaller than a cart (`ids.slice(0, 4)`), or drive the fan-out through `firstNonEmpty`/`fillTo` at one read per attempt.",
      "Running the reads in parallel will not help — the limit is on how MANY reads happen, and the read past it fails the whole invocation rather than trimming it.",
    ]
      .filter((line): line is string => line !== null)
      .join("\n");
  }
  if (details.reason === "chain_over_budget") {
    const measured = asNumber(details.measuredMs);
    const allowed = asNumber(details.allowedMs);
    const timings = Array.isArray(details.scenarioTimings)
      ? details.scenarioTimings
          .map((timing) => {
            const record = asRecord(timing);
            const name = asString(record.scenarioName);
            const ms = asNumber(record.measuredMs);
            return name
              ? `${name}: ${ms === null ? "did not finish" : `${ms}ms`}`
              : null;
          })
          .filter((line): line is string => line !== null)
      : [];
    const headline =
      measured === null
        ? `Running every fallback branch did not finish inside the ${allowed ?? "allowed"}ms available.`
        : `Running every fallback branch took ${measured}ms; ${allowed ?? "?"}ms is available after broker overhead.`;
    const basis = formatBudgetBasis(details);
    return [
      headline,
      ...(basis === null ? [] : [basis]),
      "Trim the ladder or make its reads cheaper — every branch is priced as if it fires.",
      ...(timings.length > 0 ? [bulleted(timings)] : []),
    ].join("\n");
  }
  return formatUnexpectedError(details);
};

const formatScanCostDetails: GateDetailFormatter = (details) => {
  const predicates = asStringList(details.postFetchPredicates);
  const estimated = asNumber(details.estimatedScanMs);
  const allowed = asNumber(details.allowedMs);
  if (details.reason !== "scan_refused" && details.verdict === undefined)
    return formatUnexpectedError(details);
  const lines: string[] = [];
  const pages = asNumber(details.estimatedPages);
  const boundedMetafieldValues = asNumber(details.boundedMetafieldValues);
  // "read", not "catalog scan": a bounded query has a price now too, and it
  // never scanned anything.
  if (estimated !== null && allowed !== null)
    lines.push(
      `The read is priced at ~${estimated}ms; ${allowed}ms is available.`
    );
  if (predicates.length > 0)
    lines.push(
      `These filters run after fetching, so they force the scan: ${predicates.join(", ")}.`
    );
  // An over-fetch and a too-long scan have OPPOSITE fixes: one sizes the
  // query down to what the rule renders, the other narrows what the scan
  // covers. Telling an over-fetching rule to "narrow the query server-side"
  // sends the author at the wrong axis.
  if (details.overFetch === true) {
    const ceiling = asNumber(details.renderableCeiling);
    const requested = asNumber(details.widestRequestedLimit);
    lines.push(
      ceiling !== null && requested !== null
        ? `This rule renders at most ${ceiling} products but asks Shopify for ${requested}. Size the query to \`context.slotsNeeded\` plus filtering headroom.`
        : "Size the query to `context.slotsNeeded` plus filtering headroom rather than the ceiling."
    );
  }
  // Fan-out and scan length have DIFFERENT fixes: fewer identifiers, not
  // fewer products.
  if (boundedMetafieldValues !== null && boundedMetafieldValues > 0)
    lines.push(
      `This read fetches ${boundedMetafieldValues} metafield values in one response. Fetch fewer metafield identifiers per product.`
    );
  // The scan-narrowing fix only applies when there IS a paged scan to narrow.
  // Keyed on estimatedPages, NOT estimatedScanMs: a bounded-only rule now has
  // a non-null estimate (its fan-out) and no scan to narrow, so keying on the
  // estimate would send the author at the wrong axis.
  if (pages !== null)
    lines.push(
      "Narrow the query server-side first — a collection, tags, vendor, or a price range — so the scan covers less of the catalog."
    );
  if (lines.length === 0) return null;
  return lines.join("\n");
};

const formatGraphConsistencyDetails: GateDetailFormatter = (details) => {
  const missing = asStringList(details.missingBranchNodes);
  const orphans = asStringList(details.orphanBranchNodes);
  const schemaErrors = asStringList(details.schemaErrors);
  const lines: string[] = [];
  if (missing.length > 0)
    lines.push(
      `Add a branch node for each attempt the code makes: ${missing.join(", ")}.`
    );
  if (orphans.length > 0)
    lines.push(
      `Remove graph branches the code no longer attempts: ${orphans.join(", ")}.`
    );
  if (
    Array.isArray(details.nonLiteralAttempts) &&
    details.nonLiteralAttempts.length > 0
  )
    lines.push(
      `${details.nonLiteralAttempts.length} attempt() call(s) use a computed base label — make each base a static string literal.`
    );
  if (schemaErrors.length > 0) lines.push(bulleted(schemaErrors));
  if (lines.length === 0) return formatUnexpectedError(details);
  return lines.join("\n");
};

const formatBundleSizeDetails: GateDetailFormatter = (details) => {
  const size = asNumber(details.bundleSizeBytes);
  const limit = asNumber(details.limitBytes);
  if (size === null) return formatUnexpectedError(details);
  if (limit === null) return `Bundle size: ${size.toLocaleString()} bytes`;
  return (
    `The built bundle is ${size.toLocaleString()} bytes; the ceiling is ${limit.toLocaleString()}.\n` +
    "Slim the rule's own code: Agentic Logic rules carry no third-party npm " +
    "packages, so there is nothing else in the bundle to drop."
  );
};

const formatRuntimeTimeoutDetails: GateDetailFormatter = (details) => {
  const basis = formatBudgetBasis(details);
  if (basis === null) return formatUnexpectedError(details);
  return `${basis}\nRun independent \`sdk\` reads concurrently and cut synchronous work.`;
};

const formatSuiteDetails: GateDetailFormatter = (details) => {
  if (details.reason === "suite_rejected") {
    const reasons = asStringList(details.reasons);
    return reasons.length > 0
      ? `The generated suite was rejected before running:\n${bulleted(reasons)}`
      : null;
  }
  if (details.reason === "suite_generation_incomplete") {
    return "Test generation did not complete, so there was no suite to run. Re-run the tests.";
  }
  const failures = Array.isArray(details.failures)
    ? details.failures
        .map((failure) => {
          const record = asRecord(failure);
          const name = asString(record.testName);
          const message = asString(record.message);
          return name ? `${name}${message ? ` — ${message}` : ""}` : null;
        })
        .filter((line): line is string => line !== null)
    : [];
  const lines: string[] = [];
  if (details.reason === "nondeterministic_suite")
    lines.push(
      "The suite disagreed with itself across two runs — remove ambient time and unseeded randomness from the rule or the tests."
    );
  if (failures.length > 0) lines.push(bulleted(failures));
  if (lines.length === 0) return formatUnexpectedError(details);
  return lines.join("\n");
};

const GATE_DETAIL_FORMATTERS: Partial<
  Record<AgenticLogicPublishGateKey, GateDetailFormatter>
> = {
  static_contract: formatStaticContractDetails,
  chain_cost: formatChainCostDetails,
  scan_cost: formatScanCostDetails,
  graph_consistency: formatGraphConsistencyDetails,
  bundle_size: formatBundleSizeDetails,
  runtime_timeout: formatRuntimeTimeoutDetails,
  ai_tests_passed: formatSuiteDetails,
  user_tests_passed: formatSuiteDetails,
};

/** Derived from the canonical order so the two can never drift apart. */
export const AGENTIC_GATE_SPECS: readonly WorkspaceGateSpec<AgenticLogicPublishGateKey>[] =
  AGENTIC_LOGIC_PUBLISH_GATE_ORDER.map((key) => {
    const formatter = GATE_DETAIL_FORMATTERS[key];
    return {
      key,
      shortLabel: GATE_LABELS[key].short,
      fullLabel: GATE_LABELS[key].full,
      // Only failing rows get the rewrite: a passed gate's `result` line is
      // already the right summary, and rows predating the reason vocabulary
      // fall back the same way.
      ...(formatter
        ? {
            formatDetails: (details, gate) =>
              gate.status === "failed" || gate.status === "warning"
                ? (formatter(details) ?? formatUnexpectedError(details))
                : null,
          }
        : {}),
    };
  });

/**
 * Human labels for the canonical required-test checks. Keyed by the ig-types
 * list so a new check added there fails to compile here rather than silently
 * vanishing from the breakdown.
 */
const REQUIRED_CHECK_LABELS: Record<AgenticLogicRequiredTestKey, string> = {
  valid_envelope: "Valid result envelope",
  runtime_under_budget: "Runtime within the evaluation budget",
  stable_output: "Stable output for the same invocation",
  no_leaked_secrets: "No leaked secret values in results or logs",
  fallback_safe_failure: "SDK read failure degrades safely",
  // RETIRED: never run on a fresh publish. The label stays so historical
  // publish-job rows render as a name rather than a raw key.
  shipping_claims_gated: "Shipping claims gated on resolved shipping",
  produces_output: "Produces a recommendation from platform-supplied data",
  renderable_on_surface: "Renders on the target surface",
  intent_supported_on_surface: "Asks only for actions the surface can perform",
};

/**
 * What the author should change when a check fails. Each names the specific
 * edit, not the rule that was broken — the label already says that.
 */
const REQUIRED_CHECK_HINTS: Record<AgenticLogicRequiredTestKey, string> = {
  valid_envelope:
    "Return the envelope the `evaluate` signature declares: every item " +
    "carrying the fields in the SDK types, and `children` only on bundle " +
    "items. If the scenario has nothing to serve, return an empty `items` " +
    "array rather than a partial item.",
  runtime_under_budget:
    `Cut synchronous work, or run independent \`sdk\` reads concurrently, so ` +
    `each scenario's average invocation finishes inside the evaluation budget ` +
    `this rule is held to — the tightest cap among the surfaces it can serve ` +
    `(${AGENTIC_LOGIC_TEMPLATE_RUNNER_DEFAULT_BUDGET_MS}ms by default). The ` +
    `verdict is surfaced by the "Code runs fast enough" gate.`,
  stable_output:
    "The same invocation must produce the same items in the same order. " +
    "Replace ambient randomness with `sdk.seededShuffle` and drop any " +
    "ordering that depends on wall-clock time.",
  no_leaked_secrets:
    "Keep org secret values out of what you return and what you log. Use a " +
    "secret to authenticate a call, never to build the output you return.",
  fallback_safe_failure:
    "When an `sdk` read or a helper throws or times out, either rethrow an " +
    "`Error` or return a valid empty envelope, so the placement falls back " +
    "instead of serving a half-built block.",
  // RETIRED alongside its label above — a check that never runs never fails,
  // so this hint is unreachable and exists only to keep the Record total.
  shipping_claims_gated: "This check has been retired and is no longer run.",
  produces_output:
    "This rule returned nothing under every scenario and never read the " +
    "catalog, so there is no input that would change its answer. Add a " +
    "scenario where it should recommend something, or change what it reads — " +
    "`invocation.candidates` is reserved and is never populated, so a rule " +
    "built on it can only ever return nothing.",
  renderable_on_surface:
    "Every item this rule returned is a kind the surfaces it targets cannot " +
    "draw, so the placement would show nothing. Return the kit's components " +
    "as separate `product` or `variant` items, or bind the rule to a surface " +
    "that renders that kind.",
  intent_supported_on_surface:
    "No surface can swap out a cart line today, so an item returned with " +
    '`intent: "replace"` would be offered to the shopper as an ADD beside ' +
    "the item it was meant to replace. Return the swap target with " +
    '`intent: "add"` and say what it replaces in the item\'s `reason`.',
};

export const AGENTIC_REQUIRED_CHECKS: readonly WorkspaceRequiredCheckSpec[] =
  AgenticLogicRequiredTestKeyList.map((key) => ({
    key,
    label: REQUIRED_CHECK_LABELS[key],
    hint: REQUIRED_CHECK_HINTS[key],
  }));

export const AGENTIC_GATES_CONFIG: WorkspaceGatesConfig<AgenticLogicPublishGateKey> =
  {
    order: AGENTIC_GATE_SPECS,
    deployGates: ["lambda_deploy"],
    contractGate: "static_contract",
    consoleGates: {
      required: "required_tests_passed",
      generatedSuite: "ai_tests_passed",
      userSuite: "user_tests_passed",
    },
    generatedSuiteGate: "ai_tests_generated",
    // The backend has no "nothing to run" sentinel for the user suite yet, and
    // inventing a string here would silently never match.
    noUserTestsSentinel: null,
    requiredChecks: {
      gateKey: "required_tests_passed",
      // `runRequiredTestsGate` filters `runtime_under_budget` out of
      // `required_tests_passed`'s `detailsJson.failing` and lets the dedicated
      // `runtime_timeout` gate carry that verdict, so a runtime overrun must
      // show Fail here even while the required gate is green.
      externallyGatedCheck: {
        checkKey: "runtime_under_budget",
        gateKey: "runtime_timeout",
      },
      checks: AGENTIC_REQUIRED_CHECKS,
    },
  };

const BREADCRUMBS = ["AI", "Agentic Logic", "Rule"];

export const AGENTIC_CHROME: WorkspaceChromeConfig = {
  Icon: Blocks,
  breadcrumbs: BREADCRUMBS,
  untitledLabel: "Rule",
  // The rule-builder chat persona; chat-built versions carry this byline.
  aiAuthorName: "Atlas",
  gatesHeading: "PUBLISH GATES",
  // Same store as custom shipping providers (@src/api/org-secrets), so this
  // names both consumers rather than only this domain's.
  secretsScopeMessage:
    "Every org secret is available to all Agentic Logic rules and custom shipping providers for this organization.",
  // Names the recording too, unlike CSP's: importing a version replaces the
  // fixture recording along with the code, so an author with an unsaved
  // recording is told what else is about to go.
  confirmDirtyImport:
    "Importing this version will replace the current draft — its code, tests, " +
    "and recorded fixtures. Continue?",
  // No `confirmUnpublish`: the verb is unreachable because the port supplies no
  // `unpublish` capability group, and the shell types the copy as optional for
  // exactly that reason — a domain should not author strings for verbs it
  // cannot perform.
};

export const AGENTIC_TEST_FILES: WorkspaceTestFilesConfig = {
  generatedFilename: "recommendation-rule.test.ts",
  userFilename: "recommendation-rule.user.test.ts",
};

/**
 * Both suites are files in the Advanced editor's tree: the user suite
 * editable, the generated one read-only with "Edit as my test" on each test.
 */
export const AGENTIC_EDITOR_TESTS: WorkspaceEditorTestsConfig = {
  files: AGENTIC_TEST_FILES,
  userSuiteSkeleton: AGENTIC_LOGIC_USER_SUITE_SKELETON,
};

export const AGENTIC_CONTROLS_PANEL: WorkspaceControlsPanelConfig = {
  runtimeFacts: [
    { label: "Engine", value: "Node.js 22 (ESM)", mono: true },
    {
      label: "Budget",
      value: `${AGENTIC_LOGIC_TEMPLATE_RUNNER_DEFAULT_BUDGET_MS}ms default per invocation`,
    },
    { label: "On empty", value: "Per placement assignment" },
  ],
  // The per-surface half is RENDERED, not written here: the same sentences go
  // to the authoring agent and into the injected editor types, so the panel
  // cannot end up describing a different contract from the one the model was
  // given.
  runtimeContract: [
    "Export a named `async function evaluate(invocation)` returning a result " +
      "envelope. Ambient time, randomness and network access are blocked — read " +
      "through `sdk`, and use `sdk.seededShuffle` when an order must vary. " +
      "Rules run with no third-party npm packages: use `sdk`, the standard " +
      "library, and your own helper files.",
    "",
    renderAgenticLogicRequestContract(),
  ].join("\n"),
  // No third-party code, ever. The rule runtime's no-network / no-clock
  // boundary is scoped to the rule's own files, so a bundled package would run
  // inside the rule with that boundary drawn around nothing — the bundler and
  // the save API both refuse one, and the panel says so rather than collecting
  // an entry that can only fail later.
  dependencies: {
    kind: "unsupported",
    reason:
      "Rules run with no third-party npm packages. Read commerce state through the injected SDK.",
  },
};

/** Single-file: `allowUserFiles` is false, so add/rename/delete are hidden. */
export const agenticFiles = (
  dataSurface: AgenticLogicDataSurface
): WorkspaceFilesConfig => ({
  entryFilename: AGENTIC_LOGIC_ENTRY_FILENAME,
  typesFilename: AGENTIC_LOGIC_TYPES_FILENAME,
  reservedFilenames: [
    AGENTIC_LOGIC_ENTRY_FILENAME,
    AGENTIC_LOGIC_TYPES_FILENAME,
    AGENTIC_LOGIC_SDK_TYPES_FILENAME,
  ],
  // Unreachable while `allowUserFiles` is false; kept truthful rather than empty
  // so enabling multi-file later is a one-flag change.
  newFileTemplate: "export {};\n",
  newFileBasename: "helper",
  allowUserFiles: false,
  typesModuleSource: buildAgenticLogicTypesModuleSource({
    dataSurface,
    requestFieldNotes: renderAgenticLogicRequestFieldNotes(),
    sdkFieldNotes: renderAgenticLogicSdkFieldNotes(),
  }),
  // The ambient `sdk.*` declarations, shown as a second locked tab so rule
  // authors can read what the SDK actually returns. Mirrors the companion
  // model registered on `buildAgenticLogicMonacoConfig`'s `readonlyTabs`.
  readonlyFiles: [
    {
      filename: AGENTIC_LOGIC_SDK_TYPES_FILENAME,
      source: AGENTIC_LOGIC_SDK_AMBIENT_DTS,
    },
  ],
  starterSource: AGENTIC_LOGIC_STARTER_SOURCE,
  decode: decodeAgenticLogicFiles,
});

export const AGENTIC_PUBLISH_BLOCK_COPY: Record<
  PublishBlockReason,
  WorkspacePublishBlockCopy
> = {
  unsaved_entity: {
    title: "Rule not saved",
    message: "Save the rule before publishing.",
    tooltip: "Save the rule before publishing",
  },
  syntax_invalid: {
    title: "Editor has errors",
    message: "Fix the syntax errors in the editor before publishing.",
    tooltip: "Fix the editor errors before publishing",
  },
  unsaved_changes: {
    title: "Unsaved changes",
    message:
      "You have unsaved changes. Save the draft before publishing so the gates run against what you see.",
    tooltip: "Save your changes before publishing",
  },
  failed_gate: {
    title: "Publish blocked",
    message:
      "One or more required gates have not passed. Resolve the failing gates in the Publish gates tab before publishing.",
    tooltip: "Resolve all required gates before publishing",
  },
  publishing: {
    title: "Publish in progress",
    message: "A publish is already running.",
    tooltip: "A publish is already in progress",
  },
};

export const AGENTIC_GATE_SUMMARY_ROWS: readonly {
  label: string;
  gateKey: AgenticLogicPublishGateKey;
}[] = [
  { label: "Required tests", gateKey: "required_tests_passed" },
  { label: "Chain cost", gateKey: "chain_cost" },
  { label: "Scan cost", gateKey: "scan_cost" },
];

export const agenticPublishImpactCopy = (versionToPublish: number) =>
  `Publishing switches assigned placements to v${versionToPublish} once the deploy completes.`;

/** Copy for switching the live alias to an already-published version. */
export const agenticRollbackImpactCopy = (version: number) =>
  `Publishing v${version} will switch assigned placements to that version immediately.`;

/** The two Agentic Logic rows in the publish review modal. */
export const agenticPublishReviewRows = ({
  availableSecretNames,
  dependencies,
  activeVersion,
}: {
  availableSecretNames: string[];
  dependencies: WorkspaceDependency[];
  activeVersion: WorkspaceVersion | null;
}) => {
  const previousLockHash = activeVersion?.dependencyLockHash ?? null;
  const oldLock = previousLockHash ? previousLockHash.slice(0, 12) : "none";
  const newLock = `${dependencies.length} dep${
    dependencies.length === 1 ? "" : "s"
  } locked`;

  return (
    <>
      <ReviewRow label="Org secrets available">
        {availableSecretNames.length === 0 ? (
          <IgTypography variant="body2" color="text.secondary">
            None
          </IgTypography>
        ) : (
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="flex-end"
            gap={0.5}
          >
            {availableSecretNames.map((name) => (
              <IgChip key={name} size="small" label={name} />
            ))}
          </Stack>
        )}
      </ReviewRow>

      <ReviewRow label="Dependency lock">
        <IgTypography variant="body2" sx={{ fontFamily: "monospace" }}>
          {oldLock} → {newLock}
        </IgTypography>
      </ReviewRow>
    </>
  );
};

export const agenticPublishReviewConfig = (rows: {
  availableSecretNames: string[];
  dependencies: WorkspaceDependency[];
  activeVersion: WorkspaceVersion | null;
}): WorkspacePublishReviewConfig<AgenticLogicPublishGateKey> => ({
  blockCopy: AGENTIC_PUBLISH_BLOCK_COPY,
  extraRows: agenticPublishReviewRows(rows),
  gateSummaryRows: AGENTIC_GATE_SUMMARY_ROWS,
  impactCopy: agenticPublishImpactCopy,
});

/**
 * Agentic Logic supplies gate-run and version import: no AI test generation, no
 * unpublish. Each absence degrades one affordance rather than half-breaking a
 * shared path.
 */
export const AGENTIC_CAPABILITIES: WorkspaceCapabilities = {
  canRunGates: true,
  canGenerateTests: false,
  canImportVersion: true,
  canUnpublish: false,
};

/** The subset the shared brain needs, buildable before the brain runs. */
export const agenticBrainConfig = (
  dataSurface: AgenticLogicDataSurface
): WorkspaceBrainConfig<AgenticLogicPublishGateKey> => ({
  gates: AGENTIC_GATES_CONFIG,
  files: agenticFiles(dataSurface),
  // No `generatedTestsCachePrefix`: the suite lives on the draft and is
  // inventoried server-side, so this domain never touches localStorage for it.
  editorTests: AGENTIC_EDITOR_TESTS,
  chrome: AGENTIC_CHROME,
});

export interface AgenticDomainContext {
  dataSurface: AgenticLogicDataSurface;
  publishReview: WorkspacePublishReviewConfig<AgenticLogicPublishGateKey>;
}

export const buildAgenticDomainConfig = ({
  dataSurface,
  publishReview,
}: AgenticDomainContext): WorkspaceDomainConfig<
  AgenticLogicPublishGateKey,
  AgenticLogicTabKey
> => ({
  chrome: AGENTIC_CHROME,
  gates: AGENTIC_GATES_CONFIG,
  // No bottom tabs: the workspace mounts without its bottom section, and the
  // suites the Publish gates tab used to hold are files in the editor now.
  tabs: [],
  files: agenticFiles(dataSurface),
  editorTests: AGENTIC_EDITOR_TESTS,
  testFiles: AGENTIC_TEST_FILES,
  monaco: buildAgenticLogicMonacoConfig({ dataSurface }),
  // No Agentic Logic chat workflow exists yet; the shell hides the panel.
  aiChat: null,
  controlsPanel: AGENTIC_CONTROLS_PANEL,
  publishReview,
});
